// Types for the editorial-side /electionday state.
//
// Persisted in Postgres (table `electionday_state`, row kind='editorial',
// payload jsonb). The admin handler (api/admin/electionday-editorial.ts)
// is the only writer; the public read goes via
// /api/electionday-state?kind=editorial which is edge-cached
// (s-maxage=10, stale-while-revalidate=30) so reader polls on counting
// night collapse into ~one DB read per 10 s per region.
//
// The editorial figures here are deliberately, structurally separate
// from the experimental sample's Batch[] state — they live under a
// different kind and never feed the same component. The newsroom
// call is the confirmed figure; the experimental panel's number is
// "indicative" and is rendered with its own framing further down
// the page.

export type EditorialParty = 'PL' | 'PN';

export interface EditorialNationalCall {
  /** Whichever party the newsroom has called the national result
   *  for. Null until a call is published — the public-side panel
   *  renders "Awaiting count / No call" while null. */
  party: EditorialParty | null;
  /** Free-text label the editor picks — e.g. "Projected", "Called",
   *  "Too close to call", "Awaiting count". Surfaced verbatim. */
  label: string;
  /** Newsroom's PROJECTED vote percentages. These light up first as
   *  the editorial team forms a call from sampling / partial counts;
   *  the Hero shows the margin as "Projected margin: X.X pp" while
   *  any of the official fields below are null. Renamed in the admin
   *  UI to "Projected" — the data field names stay short for
   *  backwards-compat. */
  pl_pct: number | null;
  pn_pct: number | null;
  other_pct: number | null;
  /** OFFICIAL vote percentages from the Electoral Commission. When
   *  the editor populates these, the Hero margin line switches to
   *  "Official margin: X.X pp" with stronger styling — replaces the
   *  projected line. Stays null until the commission publishes. */
  pl_pct_official: number | null;
  pn_pct_official: number | null;
  other_pct_official: number | null;
  /** PROJECTED MARGIN — a single editor-controlled figure for the
   *  hero's "Projected margin" subheading. Editor enters either the
   *  pp lead or the absolute vote lead (whichever they have at the
   *  moment); if neither is set the Hero hides the projected-margin
   *  subheading entirely. Magnitudes only — always describes the
   *  winner's lead, so positive. Replaces the previous derived
   *  pl_pct - pn_pct calc on the Hero, which required filling both
   *  per-party percentages just to see one number. */
  projected_margin_pct: number | null;
  projected_margin_votes: number | null;
  /** ISO timestamp captured server-side when the call moved out of
   *  null. Surfaced in the public UI as "Called at …". */
  calledAt: string | null;
  /** Optional editorial note shown beneath the headline call. */
  note: string | null;
}

export type DistrictStatus =
  | 'pending'
  | 'counting'
  | 'first_count'
  | 'final';

export interface DistrictEditorialResult {
  /** Raw vote counts the editor entered. Nulls mean "not yet
   *  reported" and render as "—" in the public table. */
  pl_votes: number | null;
  pn_votes: number | null;
  other_votes: number | null;
  /** Total valid ballots — for percentage display. The editor enters
   *  this directly rather than us summing pl+pn+other so a partial
   *  entry doesn't accidentally imply a complete count. */
  total_valid: number | null;
  status: DistrictStatus;
  /** Editorial estimate of count completion in this district, 0–100.
   *  Drives the map's count-progress ring on the public page —
   *  null until the newsroom starts entering a number, at which
   *  point the ring fills proportionally. Independent of the votes
   *  fields above (e.g. a district can be at 50% counted with no
   *  pl_votes entered yet if the editor is updating progress
   *  before the partial split). */
  count_progress_pct: number | null;
  /** Live 2pm turnout for this district, 0–100. Null until the
   *  newsroom has a 2pm reading from the Electoral Commission. The
   *  turnout tile grid renders this as the faint/dashed line; when
   *  null the line is absent and a "—" indicator surfaces in the
   *  info panel. */
  turnout_2pm_pct: number | null;
  /** Live closing (10pm) turnout for this district, 0–100. When
   *  set this overlays the 2022 baseline on the tile grid and the
   *  tile flips its source label from "2022 baseline" to "2026
   *  live". Until then the tile shows the 2022 closing figure from
   *  malta_districts_2022.geojson. */
  turnout_10pm_pct: number | null;
  /** Optional editorial note for this district (e.g. "Box 7 still
   *  to be opened"). */
  note: string | null;
  /** ISO timestamp the entry was last touched server-side. */
  enteredAt: string | null;
}

export type FeedEntryType = 'newsroom' | 'social';

export interface FeedEntry {
  id: string;
  type: FeedEntryType;
  /** Body text (always for newsroom; optional for social where the
   *  embed itself carries the message). */
  text: string | null;
  /** Attribution — e.g. "Lovin Malta Newsroom", "TVM live", or a
   *  Twitter handle. Surfaced as a small kicker. */
  source: string | null;
  /** Original URL of the social post. We turn this into an oEmbed-
   *  ish iframe for tweets, fb posts, etc. */
  embedUrl: string | null;
  /** Raw HTML the editor pasted from the platform's embed widget.
   *  Sanitised on render — see LiveFeed's sanitiseEmbedHtml. */
  embedHtml: string | null;
  /** Optional article link — rendered as a "Read more →" call-to-
   *  action below the body text. Newsroom entries that point to a
   *  longer write-up on lovinmalta.com or elsewhere set this. */
  articleUrl: string | null;
  /** Optional image URL — rendered at the top of the entry as a
   *  16:9 banner. Editor pastes a hosted image URL (no upload
   *  pipeline — out of scope for the composer). */
  imageUrl: string | null;
  /** ISO timestamp. Reverse-chron sort key. */
  publishedAt: string;
}

export interface ElectionDayEditorial {
  nationalCall: EditorialNationalCall;
  /** Keyed by district label (e.g. "District 1" — same labels used
   *  by the experimental BallotReadPanel dropdown). Missing districts
   *  render as "—" placeholders in the public table. */
  districts: Record<string, DistrictEditorialResult>;
  /** Feed entries — sort/filter happens in the renderer. */
  feed: FeedEntry[];
  /** Last-touched timestamp for the whole document (debug aid). */
  updatedAt: string;
}

/** Strict-but-tolerant validator used by the admin handler before
 *  it writes back to disk. Falls back to safe defaults on bad input
 *  rather than dropping the entire write. */
export function emptyEditorialState(): ElectionDayEditorial {
  return {
    nationalCall: {
      party: null,
      label: 'Awaiting count',
      pl_pct: null,
      pn_pct: null,
      other_pct: null,
      pl_pct_official: null,
      pn_pct_official: null,
      other_pct_official: null,
      projected_margin_pct: null,
      projected_margin_votes: null,
      calledAt: null,
      note: null,
    },
    districts: {},
    feed: [],
    updatedAt: new Date().toISOString(),
  };
}
